import {
  inject,
  Injectable,
} from '@angular/core';
import {jwtDecode} from 'jwt-decode';
import {ApiIframeAuthService} from '../../api/core/api-iframe-auth/api-iframe-auth.service';
import {AppTranslateService} from '../../page/i18n/translate/app-translate/app-translate.service';
import {CookieCommonService} from '../cookie-common/cookie-common.service';
import {RoleType} from '../../../../common/enums/role.enum';
import {IIframeJwtToken} from '../../../../models/token/iframe-jwt-token.model';
import {AlertService} from '../../../../modules/alert/services/alert/alert.service';

@Injectable({
  providedIn: 'root',
})
export class IframeAuthService {

  private apiIframeAuthService = inject(ApiIframeAuthService);

  private appTranslateService = inject(AppTranslateService);

  private alertService = inject(AlertService);

  private cookieCommonService = inject(CookieCommonService);

  private CONST_IFRAME_TOKEN: string = 'sng_iframe_token';

  private CONST_IFRAME_LANG: string = 'sng_iframe_lang';

  private iframeJwtTokenInfo: string = null;

  get iframeJwtToken(): any {
    return this.iframeJwtTokenInfo;
  }

  set iframeJwtToken(token: string) {
    this.iframeJwtTokenInfo = token;
  }

  /**
   * Method: Auth by iframe hash
   * @param iframeHash
   */
  iframeAuth(iframeHash: string): any {
    this.deleteIframeToken();
    return this.apiIframeAuthService.iframeAuth(iframeHash).subscribe(
      {
        next: response => {
          const {token} = response;
          if (token) {
            this.iframeJwtToken = token;
            this.cookieCommonService.saveDataInCookie(this.CONST_IFRAME_TOKEN, token); // set token
          } else {
            this.error();
          }
        },
        error: error => {
          this.error(error);
        },
      },
    );
  }

  /**
   * Method: Check iframe token
   */
  hasIframeToken(): boolean {
    return !!this.getIframeToken();
  }

  /**
   * Method: Get iframe token from cookie
   */
  getIframeToken(): string {
    return this.cookieCommonService.getFromCookie(this.CONST_IFRAME_TOKEN) || this.iframeJwtToken;
  }

  /**
   * Method: Add lang iframe in cookie
   */
  setIframeLang(lang: string): void {
    this.cookieCommonService.saveDataInCookie(this.CONST_IFRAME_LANG, lang);
  }

  /**
   * Method: Get iframe lang from cookie
   */
  getIframeLang(): string {
    return this.cookieCommonService.getFromCookie(this.CONST_IFRAME_LANG);
  }

  /**
   * Method: Delete iframe token from Cookie
   */
  deleteIframeToken(): void {
    this.cookieCommonService.deleteDataFromCookie(this.CONST_IFRAME_TOKEN);
  }

  /**
   * Method: Decoder jwt token
   * @param token
   */
  decodeIframeToken(token: string): IIframeJwtToken {
    return jwtDecode(token);
  }

  /**
   * Method: Get company discriminator from token
   */
  getCompanyDiscriminator(): RoleType {
    return this.hasIframeToken() ? this.decodeIframeToken(this.getIframeToken())?.company?.discriminator : null;
  }

  /**
   * Method: Detect insurer role for iframe token
   */
  isInsurerRoleType(): boolean {
    return this.getCompanyDiscriminator() === RoleType.INSURER;
  }

  /**
   * Method: Check access in gcc for iframe token
   */
  isInsurerGCC(): boolean {
    return this.hasIframeToken()
      ? !!this.decodeIframeToken(this.getIframeToken())?.company?.features?.show_gcc_comparison
      : false;
  }

  /**
   * Method: Error
   */
  error(error?: string): void {
    this.cookieCommonService.deleteDataFromCookie(this.CONST_IFRAME_TOKEN);
    this.alertService.error(this.appTranslateService.getTranslation('sng.error.access_denied'));
    if (error) {
      this.alertService.error(error);
    }
  }

}
